import type { BaseEvent } from "@amplitude/analytics-types";

import { customerAmplitudeInstance } from "./amplitudeInstances";

export interface OrderEditEventProperties {
    // Order identification
    orderId: string;
    bundleId?: string;

    // Original customer (not the CS agent)
    originalCustomerId: string;

    // What changed (array of change types - scalable pattern)
    // Possible values: "passenger_count", "passenger_details", "vehicle_type",
    // "departure_date", "location", "price", "customer_note", "management_note",
    // "driver_note", "flight_number"
    changeTypes: string[];
    changeCount: number;

    // Context
    orderStatus: string; // "Confirmed", "Accepted", etc.
    typeOfCustomer: "B2C" | "TravelAgent";
    tripType?: string; // "oneWay", "returnTrip", "multiCity", "dayTrip", "bundle"
    hoursUntilDeparture?: number; // Hours from edit to departure (rounded to 1 decimal)
    airportDirection?: "to_airport" | "from_airport" | "none"; // Airport transfer context
    priceChangeAmount?: number; // EUR amount of price change (can be negative for discounts)

    // Versioning
    versionBefore: number;
    versionAfter: number;

    // Timing
    hoursSincePurchase?: number; // Hours since order creation (rounded to 1 decimal)
}

/**
 * Tracks order edit events to Amplitude with proper group assignment and de-duplication.
 *
 * This function creates an Amplitude event with per-event group assignment,
 * linking the edit to the original purchase via the order group. This enables
 * tracking manual intervention rates and understanding what CS agents are editing.
 *
 * Key features:
 * - Uses BaseEvent format with per-event groups property
 * - Only this specific event is assigned to the order group (not session-wide)
 * - De-duplication via insert_id (orderId-versionAfter) prevents duplicate events on retries
 * - Non-blocking - errors don't break order save
 *
 * @param properties - Event properties including order details and changes
 *
 * @example
 * trackOrderEditEvent({
 *   orderId: "abc-123",
 *   originalCustomerId: "user-456",
 *   changeTypes: ["passenger_count", "departure_date"],
 *   changeCount: 2,
 *   versionAfter: 2,
 *   // ... other properties
 * });
 */
export function trackOrderEditEvent(properties: OrderEditEventProperties): void {
    try {
        // Use the customer Amplitude instance
        // This instance uses the same API key as the website, ensuring
        // order_edited events go to the same Amplitude project as purchase_completed events
        if (!customerAmplitudeInstance) {
            console.error("❌ Customer Amplitude instance not initialized");
            return;
        }

        // Create BaseEvent with per-event group assignment and de-duplication
        // This links ONLY this event to the order group (not session-wide)
        const event: BaseEvent = {
            event_type: "order_edited",
            event_properties: properties,
            groups: {
                order: properties.orderId, // ✅ Per-event group assignment
            },
            insert_id: `${properties.orderId}-${properties.versionAfter}`, // ✅ Stable ID for de-duplication
        };

        // Track the event to the customer instance
        customerAmplitudeInstance.track(event);

        console.debug("✅ Tracked order_edited event to customer instance", {
            orderId: properties.orderId,
            changeTypes: properties.changeTypes,
            insertId: event.insert_id,
            instance: "Management-Customer",
        });
    } catch (error) {
        console.error("❌ Failed to track order_edited event", error);
        // Don't throw - tracking failure shouldn't break order save
    }
}
